import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, Key, Info } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
}

interface EditAdminUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser;
  onSuccess: () => void;
}

export function EditAdminUserModal({ open, onOpenChange, user, onSuccess }: EditAdminUserModalProps) {
  const [newEmail, setNewEmail] = useState(user.email);
  const [updating, setUpdating] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleUpdateEmail = async () => {
    try {
      setUpdating(true);

      const { error } = await supabase.functions.invoke(
        'admin-update-user-auth',
        {
          body: {
            userId: user.id,
            email: newEmail
          }
        }
      );

      if (error) throw error;

      toast.success('Email atualizado com sucesso!');
      onSuccess();

    } catch (error) {
      console.error('Erro ao atualizar email:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar email');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetting(true);

      const { error } = await supabase.functions.invoke(
        'admin-reset-user-password',
        {
          body: { userId: user.id }
        }
      );

      if (error) throw error;

      toast.success('Email de redefinição de senha enviado!');

    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao resetar senha');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Usuário Admin</DialogTitle>
          <DialogDescription>
            Atualize as informações do usuário {user.full_name || user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Atualizar Email */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="email" className="text-base font-semibold">Atualizar Email</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Novo Email</Label>
              <Input
                id="email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="novo@email.com"
                disabled={updating}
              />
              <Button
                onClick={handleUpdateEmail}
                disabled={updating || newEmail === user.email}
                className="w-full"
              >
                {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Atualizar Email
              </Button>
            </div>
          </div>

          <Separator />

          {/* Resetar Senha */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-semibold">Resetar Senha</Label>
            </div>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Um email será enviado para <strong>{user.email}</strong> com instruções para redefinir a senha.
              </AlertDescription>
            </Alert>
            <Button
              onClick={handleResetPassword}
              disabled={resetting}
              variant="outline"
              className="w-full"
            >
              {resetting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enviar Email de Redefinição
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
